export function detectCollision(card1, card2) {
	return !(
		card1.x + card1.width <= card2.x ||
		card1.x >= card2.x + card2.width ||
		card1.y + card1.height <= card2.y ||
		card1.y >= card2.y + card2.height
	);
}

export function compactLayout(cards) {
	const sortedCards = [...cards].sort((a, b) => {
		if (a.y === b.y) return a.x - b.x;
		return a.y - b.y;
	});

	const compacted = sortedCards.map((card) => ({ ...card }));

	for (let i = 0; i < compacted.length; i++) {
		const card = compacted[i];
		const originalY = card.y;
		let newY = 0;

		while (newY < card.y) {
			const testCard = { ...card, y: newY };
			const hasCollision = compacted
				.slice(0, i)
				.some((otherCard) => detectCollision(testCard, otherCard));

			if (!hasCollision) {
				const hasCardInBetween = compacted.slice(0, i).some((otherCard) => {
					const cardSpansX =
						testCard.x < otherCard.x + otherCard.width &&
						testCard.x + testCard.width > otherCard.x;
					const cardInBetweenY = otherCard.y >= newY && otherCard.y < originalY;
					return cardSpansX && cardInBetweenY;
				});

				if (!hasCardInBetween) {
					card.y = newY;
					break;
				}
			}
			newY++;
		}
	}

	return compacted;
}

function getBounds(cards) {
	if (cards.length === 0) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };

	return {
		minX: Math.min(...cards.map((c) => c.x)),
		maxX: Math.max(...cards.map((c) => c.x + c.width)),
		minY: Math.min(...cards.map((c) => c.y)),
		maxY: Math.max(...cards.map((c) => c.y + c.height)),
	};
}

function canFitCardsAt(
	cards,
	offsetX,
	offsetY,
	excludeIds,
	allCards,
	maxColumns
) {
	for (const card of cards) {
		const testCard = { ...card, x: card.x + offsetX, y: card.y + offsetY };

		if (
			testCard.x < 0 ||
			testCard.x + testCard.width > maxColumns ||
			testCard.y < 0
		) {
			return false;
		}

		for (const otherCard of allCards) {
			if (excludeIds.includes(otherCard.id) || card.id === otherCard.id)
				continue;
			if (detectCollision(testCard, otherCard)) {
				return false;
			}
		}
	}
	return true;
}

function findSwapOpportunity(
	movedCard,
	collidingCards,
	allCards,
	maxColumns,
	originalPosition,
	pixelDelta,
	cellWidth,
	cellHeight
) {
	if (collidingCards.length === 0) return null;

	const movedDirection = {
		x: movedCard.x - originalPosition.x,
		y: movedCard.y - originalPosition.y,
	};

	const groupBounds = getBounds(collidingCards);
	const excludeIds = [movedCard.id, ...collidingCards.map((c) => c.id)];

	const horizontalOffset =
		movedDirection.x > 0 ? -movedCard.width : movedCard.width;
	const canSwapHorizontally = canFitCardsAt(
		collidingCards,
		horizontalOffset,
		0,
		excludeIds,
		allCards,
		maxColumns
	);

	if (canSwapHorizontally) {
		const collidingGroupWidth = groupBounds.maxX - groupBounds.minX;
		const swapThreshold = (collidingGroupWidth * cellWidth) / 2;

		if (Math.abs(pixelDelta.x) >= swapThreshold) {
			return {
				swapCards: collidingCards,
				offsetX: horizontalOffset,
				offsetY: 0,
			};
		}
	}

	if (movedDirection.y !== 0) {
		const verticalOffset =
			movedDirection.y > 0 ? -movedCard.height : movedCard.height;

		if (
			canFitCardsAt(
				collidingCards,
				0,
				verticalOffset,
				excludeIds,
				allCards,
				maxColumns
			)
		) {
			const collidingGroupHeight = groupBounds.maxY - groupBounds.minY;
			const swapThreshold = (collidingGroupHeight * cellHeight) / 2;

			if (Math.abs(pixelDelta.y) >= swapThreshold) {
				return {
					swapCards: collidingCards,
					offsetX: 0,
					offsetY: verticalOffset,
				};
			}
		}
	}

	return null;
}

export function resolveCollisions(
	movedCard,
	allCards,
	maxColumns,
	pixelDelta,
	cellWidth,
	cellHeight
) {
	const cards = allCards.map((card) => ({ ...card }));
	const movedIndex = cards.findIndex((c) => c.id === movedCard.id);

	if (movedIndex === -1) return cards;

	const originalCard = { ...cards[movedIndex] };
	cards[movedIndex] = { ...movedCard };

	if (movedCard.x < 0) cards[movedIndex].x = 0;
	if (movedCard.x + movedCard.width > maxColumns) {
		cards[movedIndex].x = maxColumns - movedCard.width;
	}
	if (movedCard.y < 0) cards[movedIndex].y = 0;

	const moved = cards[movedIndex];

	const isHorizontalResize =
		moved.x === originalCard.x &&
		moved.y === originalCard.y &&
		moved.width !== originalCard.width &&
		moved.height === originalCard.height;

	const collidingCards = cards
		.filter((card, idx) => idx !== movedIndex && detectCollision(moved, card))
		.sort((a, b) => {
			if (a.y === b.y) return a.x - b.x;
			return a.y - b.y;
		});

	if (collidingCards.length > 0 && pixelDelta && cellWidth && cellHeight) {
		const swapOpportunity = findSwapOpportunity(
			moved,
			collidingCards,
			cards,
			maxColumns,
			{ x: originalCard.x, y: originalCard.y },
			pixelDelta,
			cellWidth,
			cellHeight
		);

		if (swapOpportunity) {
			const swappedCardsBounds = getBounds(swapOpportunity.swapCards);
			const swappedCardIndices = [];

			for (const card of swapOpportunity.swapCards) {
				const cardIndex = cards.findIndex((c) => c.id === card.id);
				cards[cardIndex].x += swapOpportunity.offsetX;
				cards[cardIndex].y += swapOpportunity.offsetY;
				swappedCardIndices.push(cardIndex);
			}

			if (swapOpportunity.offsetX !== 0) {
				const movedDirection = moved.x - originalCard.x;
				if (movedDirection > 0) {
					cards[movedIndex].x = swappedCardsBounds.maxX - moved.width;
				} else if (movedDirection < 0) {
					cards[movedIndex].x = swappedCardsBounds.minX;
				}
			}

			if (swapOpportunity.offsetY !== 0) {
				const movedDirection = moved.y - originalCard.y;
				if (movedDirection < 0) {
					cards[movedIndex].y = swappedCardsBounds.minY;
				} else if (movedDirection > 0) {
					cards[movedIndex].y = swappedCardsBounds.minY;
				}
			}

			const stillColliding = cards
				.filter(
					(card, idx) =>
						idx !== movedIndex && detectCollision(cards[movedIndex], card)
				)
				.sort((a, b) => {
					if (a.y === b.y) return a.x - b.x;
					return a.y - b.y;
				});

			if (stillColliding.length > 0) {
				let safeY = cards[movedIndex].y;
				let foundSafe = false;

				for (
					let testY = cards[movedIndex].y;
					testY < cards[movedIndex].y + 50;
					testY++
				) {
					const testCard = { ...cards[movedIndex], y: testY };
					const hasCollision = cards
						.filter((card, idx) => idx !== movedIndex)
						.some((card) => detectCollision(testCard, card));

					if (!hasCollision) {
						safeY = testY;
						foundSafe = true;
						break;
					}
				}

				if (foundSafe) {
					cards[movedIndex].y = safeY;
				}
			}

			const pushDownRecursively = (cardIndex, excludeIndices) => {
				const card = cards[cardIndex];
				const cascadeCollisions = cards
					.filter(
						(c, idx) => !excludeIndices.has(idx) && detectCollision(card, c)
					)
					.sort((a, b) => a.y - b.y);

				for (const cascadeCard of cascadeCollisions) {
					const cascadeIndex = cards.findIndex((c) => c.id === cascadeCard.id);
					if (cards[cascadeIndex].y < card.y + card.height) {
						cards[cascadeIndex].y = card.y + card.height;
						excludeIndices.add(cascadeIndex);
						pushDownRecursively(cascadeIndex, excludeIndices);
					}
				}
			};

			for (const swappedIndex of swappedCardIndices) {
				const excludeIndices = new Set([movedIndex, swappedIndex]);
				pushDownRecursively(swappedIndex, excludeIndices);
			}

			return cards;
		} else {
			const movedDirection = {
				x: moved.x - originalCard.x,
				y: moved.y - originalCard.y,
			};

			const excludeIds = [moved.id, ...collidingCards.map((c) => c.id)];
			const primaryDirection =
				Math.abs(movedDirection.x) > Math.abs(movedDirection.y)
					? 'horizontal'
					: 'vertical';

			if (primaryDirection === 'horizontal' && movedDirection.x !== 0) {
				const horizontalOffset =
					movedDirection.x > 0 ? -moved.width : moved.width;
				const canSwapHorizontally = canFitCardsAt(
					collidingCards,
					horizontalOffset,
					0,
					excludeIds,
					cards,
					maxColumns
				);
				if (canSwapHorizontally) {
					cards[movedIndex] = originalCard;
					return cards;
				}
			} else if (primaryDirection === 'vertical' && movedDirection.y !== 0) {
				const verticalOffset =
					movedDirection.y > 0 ? -moved.height : moved.height;
				const canSwapVertically = canFitCardsAt(
					collidingCards,
					0,
					verticalOffset,
					excludeIds,
					cards,
					maxColumns
				);
				if (canSwapVertically) {
					cards[movedIndex] = originalCard;
					return cards;
				}
			}
		}
	}

	const pushDownRecursively = (cardIndex, excludeIndices) => {
		const card = cards[cardIndex];
		const cascadeCollisions = cards
			.filter((c, idx) => !excludeIndices.has(idx) && detectCollision(card, c))
			.sort((a, b) => a.y - b.y);

		for (const cascadeCard of cascadeCollisions) {
			const cascadeIndex = cards.findIndex((c) => c.id === cascadeCard.id);
			if (cards[cascadeIndex].y < card.y + card.height) {
				cards[cascadeIndex].y = card.y + card.height;
				excludeIndices.add(cascadeIndex);
				pushDownRecursively(cascadeIndex, excludeIndices);
			}
		}
	};

	if (isHorizontalResize && moved.width > originalCard.width) {
		const cardsToReposition = [];

		for (const colliding of collidingCards) {
			const cardIndex = cards.findIndex((c) => c.id === colliding.id);
			const card = cards[cardIndex];

			const isToRight = card.x >= originalCard.x + originalCard.width;
			const onSameRow = card.y === moved.y;

			if (isToRight && onSameRow) {
				const newX = moved.x + moved.width;

				if (newX + card.width <= maxColumns) {
					const testCard = { ...card, x: newX };
					const hasCollisionAtNewPos = cards.some(
						(c, idx) =>
							idx !== movedIndex &&
							idx !== cardIndex &&
							detectCollision(testCard, c)
					);

					if (!hasCollisionAtNewPos) {
						cards[cardIndex].x = newX;
						continue;
					}
				}
			}

			cardsToReposition.push(colliding);
		}

		for (const colliding of cardsToReposition) {
			const cardIndex = cards.findIndex((c) => c.id === colliding.id);
			const card = cards[cardIndex];

			const pushDown = moved.y + moved.height;
			card.y = pushDown;

			const excludeIndices = new Set([movedIndex, cardIndex]);
			pushDownRecursively(cardIndex, excludeIndices);
		}
	} else {
		for (const colliding of collidingCards) {
			const cardIndex = cards.findIndex((c) => c.id === colliding.id);
			const card = cards[cardIndex];

			const pushDown = moved.y + moved.height;
			card.y = pushDown;

			const excludeIndices = new Set([movedIndex, cardIndex]);
			pushDownRecursively(cardIndex, excludeIndices);
		}
	}

	return compactLayout(cards);
}

export function findAvailablePosition(cards, width, height, maxColumns) {
	for (let y = 0; y < 100; y++) {
		for (let x = 0; x <= maxColumns - width; x++) {
			const testCard = { id: 'test', x, y, width, height };
			const hasCollision = cards.some((card) =>
				detectCollision(testCard, card)
			);

			if (!hasCollision) {
				return { x, y };
			}
		}
	}

	return {
		x: 0,
		y: cards.reduce((max, card) => Math.max(max, card.y + card.height), 0),
	};
}

export function reflowCardsForColumns(cards, maxColumns) {
	const reflowed = [];

	const sortedCards = [...cards].sort((a, b) => {
		if (a.y === b.y) return a.x - b.x;
		return a.y - b.y;
	});

	for (const card of sortedCards) {
		let newCard = { ...card };

		if (newCard.x + newCard.width > maxColumns) {
			newCard.x = Math.max(0, maxColumns - newCard.width);
		}

		if (newCard.width > maxColumns) {
			newCard.width = maxColumns;
		}

		let placed = false;
		for (let y = 0; y < 100 && !placed; y++) {
			for (let x = 0; x <= maxColumns - newCard.width && !placed; x++) {
				const testCard = { ...newCard, x, y };
				const hasCollision = reflowed.some((c) => detectCollision(testCard, c));

				if (!hasCollision) {
					newCard = testCard;
					placed = true;
				}
			}
		}

		reflowed.push(newCard);
	}

	return compactLayout(reflowed);
}
